import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import moment from 'moment-timezone';
import { createCoreAxiosInstance, createReportAxiosInstance } from 'createAxiosInstance';

import { colors } from 'styles/theme.colors';

import { meta } from 'config/integrations';

import { ALL_LOCATIONS } from 'constants/cubohActionBar';
import { SKIP_THE_DISHES, STOREFRONT_APP_NAMES } from 'constants/integrations';
import {
  POS_ERROR,
  ANALYTICS,
  ORDER,
  ITEM,
  MODIFIER,
  CONTROL,
  ORDER_DETAIL,
  ADJUSTMENT,
  CANCELLATIONS,
  STORE_STATUS_UPDATED,
  POS_EXCEPTION,
  CN_CUBE_ROW_LIMIT,
  CN_REPORTS_TYPE,
  CN_TIME_DIMENSION,
} from 'constants/reporting';
import { INACTIVE } from 'constants/cuboh';

import { requestChunker } from 'helpers/format.cuboh';
import { timeZoneOffset, dateToUTC } from 'helpers/dateTime';

dayjs.extend(customParseFormat);

export const getAnalyticsColorsFromApp = apps => {
  const graphColors = Object.values(colors.graph);

  if (!apps?.length) {
    // include 1 default color
    return [
      {
        fill: graphColors[0],
      },
    ];
  }

  let currentColorIndex = 0;
  const maxColorIndex = graphColors.length - 1;

  const colorsByApp = apps.map(app => {
    const fill = graphColors[currentColorIndex];

    currentColorIndex += 1;
    if (currentColorIndex > maxColorIndex) {
      currentColorIndex = 0;
    }

    return { fill, app };
  });
  return colorsByApp;
};

export function getQueryFilters({ selectedCompanies, selectedMerchants, orderID, reportType }) {
  const memberValue = 'APIOrder.merchantId';

  const selectedCompaniesInt = selectedCompanies.map(id => Number(id));
  const selectedMerchantsInt = selectedMerchants.map(id => Number(id));

  const filters = {
    [ORDER]: [
      {
        member: memberValue,
        operator: 'equals',
        values: selectedMerchantsInt,
      },
      {
        member: 'APIStatus.description',
        operator: 'notEquals',
        values: [INACTIVE],
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
    [CONTROL]: [
      {
        member: 'APIAuditrecord.companyId',
        operator: 'equals',
        values: selectedCompaniesInt,
      },
      {
        member: 'APIAuditrecord.eventName',
        operator: 'equals',
        values: [STORE_STATUS_UPDATED],
      },
      {
        member: 'APIAuditrecord.merchantId',
        operator: 'equals',
        values: selectedMerchantsInt,
      },
    ],
    [POS_ERROR]: [
      {
        member: 'APIAuditrecord.companyId',
        operator: 'equals',
        values: selectedCompaniesInt,
      },
      {
        member: 'APIAuditrecord.eventName',
        operator: 'equals',
        values: [POS_EXCEPTION],
      },
      {
        member: 'APIAuditrecord.merchantId',
        operator: 'equals',
        values: selectedMerchantsInt,
      },
    ],
    [ANALYTICS]: [
      {
        member: memberValue,
        operator: 'equals',
        values: selectedMerchantsInt,
      },
      {
        member: 'APIStatus.description',
        operator: 'notEquals',
        values: [INACTIVE],
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
    [ORDER_DETAIL]: [
      {
        member: 'APIOrderDetail.orderId',
        operator: 'equals',
        values: [`${orderID}`],
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
    [ITEM]: [
      {
        member: memberValue,
        operator: 'equals',
        values: selectedMerchantsInt,
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
    [ADJUSTMENT]: [
      {
        member: memberValue,
        operator: 'equals',
        values: selectedMerchantsInt,
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
    [CANCELLATIONS]: [
      {
        member: memberValue,
        operator: 'equals',
        values: selectedMerchantsInt,
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
    [MODIFIER]: [
      {
        member: memberValue,
        operator: 'equals',
        values: selectedMerchantsInt,
      },
      {
        member: 'APIOrderDetail.subItemJson',
        operator: 'set',
      },
      {
        member: 'APIOrder.app',
        operator: 'notEquals',
        values: STOREFRONT_APP_NAMES,
      },
    ],
  };
  return filters[reportType];
}

export const fetchActiveApps = async merchants => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const { data } = await requestChunker(
      axiosInstance,
      'api/v2/integrations?merchantIDs=',
      merchants,
    );

    data.apps = [...new Set(data.apps)];

    return data.apps.map(app => {
      const appToUse = app === 'SKIP THE DISHES' ? SKIP_THE_DISHES : app;

      const info = meta.find(config => config.appname === appToUse);
      return {
        text: info?.name ?? appToUse,
        value: info?.appname ?? appToUse,
        imageUrl: info?.imageUrl ?? appToUse,
      };
    });
  } catch (error) {
    return null;
  }
};

export function mapDefaultMerchants(selectedCompanyMerchants, selectedCompanyID) {
  return (
    selectedCompanyMerchants?.filter(merchant => {
      // Filter merchants that match for All Locations selection
      if (selectedCompanyID === ALL_LOCATIONS) {
        return selectedCompanyID === ALL_LOCATIONS;
      }
      /** Filter merchants that match with a single location company OR
       *  Filter Merchants the have a selectedCompanyID and no merchant
       *  selected (Parent Company situation)
       */
      return merchant.companyID === selectedCompanyID;
    }) ?? []
  ).map(merchant => `${merchant.id}`);
}

export function getQueryOrderBy(reportType) {
  const orderBy = {
    [ORDER]: {
      'APIOrder.created': 'desc',
    },
    [CONTROL]: {
      'APIAuditrecord.createdAt': 'desc',
    },
    [POS_ERROR]: {
      'APIAuditrecord.createdAt': 'desc',
    },
    [ORDER_DETAIL]: {
      'APIOrderDetail.created': 'desc',
    },
    [ANALYTICS]: {
      'APIOrder.created': 'desc',
    },
    [ITEM]: {
      'APIOrderDetail.created': 'desc',
    },
    [MODIFIER]: {
      'APIOrderDetail.created': 'desc',
    },
    [ADJUSTMENT]: {
      'APIAdjustment.createdAt': 'desc',
    },
    [CANCELLATIONS]: {
      'APIOrder.created': 'desc',
    },
  };

  return orderBy[reportType];
}

export function getQueryDimensions(reportType) {
  const dimensions = {
    [CONTROL]: [
      'APIAuditrecord.payload',
      'APIAuditrecord.createdAt',
      'APIAuditrecord.eventName',
      'APIAuditrecord.userId',
      'APIAuditrecord.companyId',
    ],
    [POS_ERROR]: [
      'APIAuditrecord.payload',
      'APIAuditrecord.createdAt',
      'APIAuditrecord.eventName',
      'APIAuditrecord.companyId',
    ],
    [ORDER_DETAIL]: [
      'APIOrderDetail.orderId',
      'APIOrderDetail.created',
      'APIOrderDetail.item',
      'APIOrderDetail.subItemJson',
      'APIOrderDetail.subItem',
      'APIOrderDetail.instructions',
      'APIOrderDetail.code',
      'APIOrderDetail.price',
      'APIOrderDetail.quantity',
    ],
    [ITEM]: [
      'APIOrderDetail.item',
      'APIOrderDetail.price',
      'APIOrderDetail.quantity',
      'APIOrder.merchantId',
      'APIOrderDetail.created',
      'APIOrder.app',
      'APIOrder.statusOrder',
    ],
    [MODIFIER]: [
      'APIOrderDetail.item',
      'APIOrderDetail.subItemJson',
      'APIOrder.app',
      'APIOrder.statusOrder',
      'APIOrderDetail.quantity',
      'APIMerchant.name',
    ],
    [ADJUSTMENT]: [
      'APIAdjustment.amount',
      'APIAdjustment.orderId',
      'APIAdjustment.type',
      'APIAdjustment.reason',
      'APIAdjustment.staffName',
      'APIAdjustment.createdAt',
      'APIOrder.fulfillmentType',
      'APIOrder.fulfillmentTime',
      'APIOrder.nameClient',
      'APIOrder.paymentType',
      'APIOrder.code',
      'APIOrder.app',
      'APIOrder.id',
      'APIOrder.merchantId',
      'APIOrder.statusOrder',
      'APIOrder.created',
      'APIMerchant.name',
    ],
    [CANCELLATIONS]: [
      'APIOrderCancellationData.created_at',
      'APIOrderCancellationData.cancelled_by',
      'APIOrder.fulfillmentType',
      'APIOrder.fulfillmentTime',
      'APIOrder.nameClient',
      'APIOrder.paymentType',
      'APIOrder.code',
      'APIOrder.app',
      'APIOrder.id',
      'APIOrder.merchantId',
      'APIOrder.statusOrder',
      'APIOrder.created',
      'APIMerchant.name',
      'APIOrder.grandTotal',
    ],
    [ORDER]: [
      'APIOrder.merchantId',
      'APIOrder.id',
      'APIOrder.code',
      'APIOrder.nameClient',
      'APIOrder.app',
      'APIOrder.tax',
      'APIOrder.tip',
      'APIOrder.discount',
      'APIOrder.deliveryFee',
      'APIOrder.grandTotal',
      'APIOrder.subtotal',
      'APIOrder.paymentType',
      'APIOrder.fulfillmentTime',
      'APIOrder.fulfillmentType',
      'APIOrder.created',
      'APIMerchant.name',
      'APICompany.name',
      'APIStatus.description',
      'APIOrderCancellationData.cancelled_by',
    ],
    [ANALYTICS]: [
      'APIOrder.merchantId',
      'APIOrder.id',
      'APIOrder.groupId',
      'APIOrder.app',
      'APIOrder.tax',
      'APIOrder.tip',
      'APIOrder.code',
      'APIOrder.discount',
      'APIOrder.deliveryFee',
      'APIOrder.grandTotal',
      'APIOrder.subtotal',
      'APIOrder.created',
      'APIOrder.fulfillmentType',
      'APIOrder.storefrontServiceFee',
      'APIMerchant.name',
      'APIStatus.description',
      'APIOrder.statusOrder',
      'APIOrdergroup.id',
      'APIOrdergroup.meta',
      'APIOrdergroup.storefrontServiceFeeProcessedAs',
      'APIOrdergroup.kioskConvenienceFeeProcessedAs',
    ],
  };

  return dimensions[reportType];
}

export function getQueryByMeasures(reportType) {
  const measures = {
    item: ['APIOrderDetail.count'],
  };

  return measures[reportType];
}

export function getQueryTimeDimension(reportType) {
  const timeDimension = {
    [ORDER]: 'APIOrder.created',
    [CONTROL]: 'APIAuditrecord.createdAt',
    [POS_ERROR]: 'APIAuditrecord.createdAt',
    [ORDER_DETAIL]: 'APIOrderDetail.created',
    [ANALYTICS]: 'APIOrder.created',
    [ITEM]: 'APIOrder.created',
    [MODIFIER]: 'APIOrder.created',
    [ADJUSTMENT]: 'APIAdjustment.createdAt',
    [CANCELLATIONS]: 'APIOrderCancellationData.created_at',
  };

  return timeDimension[reportType];
}

export function mapResultData(result, selectedTimezone, key) {
  /** Ensures uniqueness in the returned dataset.
   * The resulting value is an iterable object so we must use forEach instead of Map here.
   * */
  const resultSet = new Set(result);
  const resultArray = [];
  resultSet.forEach(order => {
    const offset = timeZoneOffset(selectedTimezone, order[key]);
    // Convert the time back to the correct timezone
    resultArray.push({
      ...order,
      [key]: moment.utc(order[key]).utcOffset(offset).format('YYYY/MM/DD hh:mm:ss a'),
      [`${[key]}.hour`]: moment
        .utc(order[`${[key]}.hour`])
        .utcOffset(offset)
        .format('YYYY/MM/DD hh:mm:ss a'),
    });
  });

  return resultArray;
}

function splitDateRangeIntoMonthlyChunks(start, end) {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const chunks = [];

  if (startDate.isSame(endDate, 'month')) {
    return [{ start, end }];
  }

  const firstMonthEnd = startDate.endOf('month');
  chunks.push({
    start,
    end: firstMonthEnd.format('YYYY-MM-DDTHH:mm:ss'),
  });

  let currentMonth = startDate.add(1, 'month').startOf('month');
  while (currentMonth.isBefore(endDate, 'month')) {
    chunks.push({
      start: currentMonth.format('YYYY-MM-DDTHH:mm:ss'),
      end: currentMonth.endOf('month').format('YYYY-MM-DDTHH:mm:ss'),
    });
    currentMonth = currentMonth.add(1, 'month');
  }

  const lastMonthStart = endDate.startOf('month');
  if (lastMonthStart.isAfter(startDate, 'month')) {
    chunks.push({
      start: lastMonthStart.format('YYYY-MM-DDTHH:mm:ss'),
      end,
    });
  }

  return chunks;
}

export function buildQueries(queryData) {
  const { reportType, order, selectedDateRange, selectedTimezone } = queryData;
  const filters = getQueryFilters(queryData);
  const startOffset = timeZoneOffset(selectedTimezone, selectedDateRange.start);
  const endOffset = timeZoneOffset(selectedTimezone, selectedDateRange.end);
  const { start, end } = dateToUTC(selectedDateRange, startOffset, endOffset);

  // The "member value" should be the filter with the longest array of values
  const sortedFilters = filters
    .slice()
    .sort((filterA, filterB) => (filterB?.values?.length || 0) - (filterA?.values?.length || 0));
  const memberValueFilters = sortedFilters[0].values;
  const filterToChunk = sortedFilters[0];

  const memberValueFilterChunks = [];
  const chunkSize = 250;
  for (let i = 0; i < memberValueFilters.length; i += chunkSize) {
    const chunk = memberValueFilters.slice(i, i + chunkSize);
    // Use the filter with the longest array of values, not always the first filter
    const otherFilters = filters.filter(filter => filter !== filterToChunk);
    memberValueFilterChunks.push([{ ...filterToChunk, values: chunk }, ...otherFilters]);
  }

  const dimensions = getQueryDimensions(reportType);
  const measures = getQueryByMeasures(reportType);
  const timeDimension = getQueryTimeDimension(reportType);

  const monthlyChunks = splitDateRangeIntoMonthlyChunks(start, end);

  const queries = monthlyChunks.flatMap(monthChunk =>
    memberValueFilterChunks.map(filterChunk => ({
      measures,
      timeDimensions: [
        {
          dimension: timeDimension,
          granularity: 'day',
          dateRange: [monthChunk.start, monthChunk.end],
        },
      ],
      order,
      dimensions,
      limit: '10000', // This mirrors the limit in cube
      filters: filterChunk,
      renewQuery: true,
    })),
  );

  return queries;
}

export async function queryCube(
  reportType,
  orderID,
  selectedDateRange,
  selectedCompanies,
  selectedMerchants,
  selectedTimezone,
) {
  const order = getQueryOrderBy(reportType);
  const dateTimeKey = Object.keys(order)[0];

  // Determine Cube we are querying so we can query each "table" using transaction / reporting db
  const cubeQuery = Object.keys(order)[0].split('.')[0];

  const queryBuildData = {
    reportType,
    order,
    selectedCompanies,
    selectedMerchants,
    selectedDateRange,
    selectedTimezone,
    orderID,
  };

  const queries = buildQueries(queryBuildData);

  const axiosInstance = await createReportAxiosInstance();

  /** CubeJS will respond with a "continue wait" if the db query takes over 4 seconds to complete.
   *  Any exact requests called in sequence will not create a new query request it will check to
   *  see if the query has finished and where it resides in the queue. body parameter will
   *  define which database is queried for reporting. The custom dimension added to each query
   *  hitting cubejs ensures that we will not hit the same cacheKey on the cubeJS api redis
   *  cluster and appropriately returns the data based on which dataSource is hit.
   */
  const reportingDataLoop = async database => {
    let queryData = [];

    /* eslint-disable-next-line no-restricted-syntax */
    /* eslint-disable no-restricted-syntax */
    /* eslint-disable no-await-in-loop */
    /* eslint-disable no-param-reassign */
    for (const query of queries) {
      let data;

      do {
        data = await axiosInstance.get('', {
          params: {
            query: {
              ...query,
              dimensions: [...query.dimensions, `${cubeQuery}.${database}`],
            },
            appID: database,
          },
          /** After updating axios the params got split up and became too big
           *  solution taken from here:
           *  https://github.com/axios/axios/issues/5630#issue-1653334558
           */
          paramsSerializer: params =>
            (Object.keys(params)?.filter(key => params[key] !== undefined) ?? [])
              .map(key => {
                if (typeof params[key] === 'object') {
                  params[key] = JSON.stringify(params[key]);
                }
                if (Array.isArray(params[key])) {
                  return params[key].map(v => `${key}[]=${encodeURIComponent(v)}`).join('&');
                }
                return `${key}=${encodeURIComponent(params[key])}`;
              })
              .join('&'),
        });
        // Guard: CubeJS may return HTTP 200 with an error body (e.g. {"error": "Forbidden"})
        // for unauthorized requests. Without this check, data.data.data is falsy and the
        // do-while loops forever. "Continue wait" is the expected polling response — allow it.
        if (data.data?.error && data.data.error !== 'Continue wait') {
          const err = new Error(data.data.error);
          err.cuboh403 = data.data.error === 'Forbidden';
          throw err;
        }
      } while (!data.data.data);

      queryData = [...queryData, ...data.data.data];
    }

    return queryData;
  };
  const cubeJSData = await Promise.all([
    reportingDataLoop('transactions'),
    reportingDataLoop('reporting'),
  ]);

  const reportData = [...cubeJSData[0], ...cubeJSData[1]];
  return mapResultData(reportData, selectedTimezone, dateTimeKey);
}

/** Bring up Order Code from AuditRecord query so it can be matched
 *  in Analytics and Order History Report
 *  */
export const exceptionOrderCodes = exception => {
  if (!exception) return [];
  return exception.map(issue => issue['APIAuditrecord.payload']?.order_code);
};

export function getDateRangeForPreset(value) {
  const today = dayjs();
  switch (value) {
    case CN_TIME_DIMENSION.yesterday.value: {
      const d = today.subtract(1, 'day').toDate();
      return [d, d];
    }
    // The seven days before today, as Cube's relative "last 7 days" range does.
    case CN_TIME_DIMENSION.lastSevenDays.value:
      return [today.subtract(7, 'day').toDate(), today.subtract(1, 'day').toDate()];
    case CN_TIME_DIMENSION.thisMonth.value:
      return [today.startOf('month').toDate(), today.toDate()];
    case CN_TIME_DIMENSION.lastMonth.value:
      return [
        today.subtract(1, 'month').startOf('month').toDate(),
        today.subtract(1, 'month').endOf('month').toDate(),
      ];
    case CN_TIME_DIMENSION.thisYear.value:
      return [today.startOf('year').toDate(), today.toDate()];
    case CN_TIME_DIMENSION.lastYear.value:
      return [
        today.subtract(1, 'year').startOf('year').toDate(),
        today.subtract(1, 'year').endOf('year').toDate(),
      ];
    default:
      return [today.toDate(), today.toDate()];
  }
}

export function getTotalsQuery({ filters, showDirectDinerFeeShare, timeDimensions }) {
  const { aov, discounts, gfs, orderCount, directDinerFeeShare, subtotal, taxes, tips } =
    CN_REPORTS_TYPE;
  const defaultMeasures = [subtotal, tips, taxes, discounts, gfs, orderCount, aov];

  return {
    limit: CN_CUBE_ROW_LIMIT,
    filters,
    measures: showDirectDinerFeeShare ? [...defaultMeasures, directDinerFeeShare] : defaultMeasures,
    timeDimensions: timeDimensions.map(({ granularity, ...item }) => item),
  };
}

export function getGranularity(dateFilter) {
  if (typeof dateFilter === 'string') {
    if (dateFilter === CN_TIME_DIMENSION.yesterday.value) {
      return 'hour';
    }
    if (
      dateFilter === CN_TIME_DIMENSION.lastYear.value ||
      dateFilter === CN_TIME_DIMENSION.thisYear.value
    ) {
      return 'month';
    }
    return 'day';
  }

  const day1 = dayjs(dateFilter[1]);
  const diff = day1.diff(dateFilter[0], 'day');

  if (diff === 0) return 'hour';
  if (diff <= 31) return 'day';
  if (diff <= 365) return 'month';
  return 'year';
}

export function formatXAxis({ tick, isHourlyAverage, dateRange, granularity }) {
  const GRANULARITY_FORMAT = {
    hour: 'ha',
    day: dateRange === CN_TIME_DIMENSION.lastSevenDays.value ? 'dd' : 'DD',
    month: 'MMM',
    year: 'YYYY',
  };
  const dateFormat = GRANULARITY_FORMAT[granularity];

  if (isHourlyAverage) return dayjs(tick, 'H').format('ha');

  return dayjs(tick).format(dateFormat);
}

export function formatYAxis({ tick, isMoney }) {
  let formattedTick = tick;
  const unit = isMoney ? '$' : '';
  const millions = tick / 1000000;
  const thousands = tick / 1000;

  if (millions >= 1) {
    formattedTick = `${millions}M`;
  } else if (thousands >= 1) {
    formattedTick = `${thousands}K`;
  }

  return `${unit}${formattedTick}`;
}

export const DEFAULT_Y_AXIS_TICK_STYLE = {
  textAnchor: 'start',
  dx: -50,
  fill: 'currentColor',
  fontSize: 'var(--mantine-font-size-xs)',
};

export function getYAxisProps({ isMoney = true, tickStyle = DEFAULT_Y_AXIS_TICK_STYLE } = {}) {
  return {
    tickFormatter: tick => formatYAxis({ tick, isMoney }),
    tick: tickStyle,
  };
}

export function formatChartToolTip({ label, dateRange, dateFormat = 'ddd, MMM D' }) {
  if (dateRange.includes('year')) {
    dateFormat = 'MMM YYYY';
  } else if (Array.isArray(dateRange)) {
    const day1 = dayjs(dateRange[1]);
    const diff = day1.diff(dateRange[0], 'day');
    if (diff > 365) dateFormat = 'YYYY';
  }
  const parsedLabel = dayjs(label);
  return parsedLabel.isValid() ? parsedLabel.format(dateFormat) : label;
}

export function setTimeDimension(timeDimensionArray, timeDimension) {
  return timeDimensionArray.map(item => ({
    ...item,
    dimension: timeDimension,
  }));
}
